// Scroll-triggered motion (spec D7). The transitions themselves are CSS, on the state
// classes below (src/css/idealex-1077d4.webflow.css, "Motion"); this module only decides
// when an element enters or leaves its trigger band.

const SELECTORS = {
  reveal: [
    ".big-banner",
    ".illustration-and-text-block",
    ".big-logos-banner",
    ".icon-and-text-block",
    ".value-block",
    ".accordion-item",
    ".contact-block",
    ".form-content-wrapper",
    ".form-image",
  ].join(", "),
  // Each home section, the fixed tint layer that shows while it is in view, and the
  // class the body takes meanwhile.
  tints: [
    { section: "#Presentation", tint: ".bg-color-green-light-1", bodyClass: "is-tinted-green" },
    { section: "#Services", tint: ".bg-color-yellow-light", bodyClass: "is-tinted-yellow" },
    { section: "#Nos-valeurs", tint: ".bg-color-blue-light-1", bodyClass: "is-tinted-blue" },
  ],
};

const REVEALED = "is-revealed";
const TINTED = "is-tinted";

/**
 * Reveal: each element fades and slides in once, the first time it overlaps the band
 * from 10% to 90% of the viewport height (edges included). Elements already in the band
 * when the page loads play at load.
 */
export function initReveal() {
  const elements = document.querySelectorAll(SELECTORS.reveal);
  if (elements.length === 0) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(REVEALED);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: "-10% 0px -10% 0px", threshold: 0 },
  );
  for (const element of elements) observer.observe(element);
}

/**
 * Section tints (home): a section is in view while it spans the horizontal middle line
 * of the viewport; its tint fades in when it enters and out when it leaves, every time.
 * That band is a line, which an IntersectionObserver could only express as a zero-height
 * root (rootMargin -50% top and bottom), an edge case of its geometry; so the test runs
 * on scroll and resize instead, as the edge-inclusive overlap the former runtime used.
 * The body takes the same colour (CSS, "Section tints"): Safari on iOS draws the page
 * under its status bar and toolbar but keeps fixed layers out of them, so without it
 * those areas would stay the dark page colour while the tint shows everywhere else.
 */
export function initTints() {
  const pairs = SELECTORS.tints
    .map(({ section, tint, bodyClass }) => ({
      section: document.querySelector(section),
      tint: document.querySelector(tint),
      bodyClass,
    }))
    .filter(({ section, tint }) => section && tint);
  if (pairs.length === 0) return;
  const { body } = document;
  const update = () => {
    const { clientWidth, clientHeight } = document.documentElement;
    const middle = clientHeight / 2;
    for (const { section, tint, bodyClass } of pairs) {
      const rect = section.getBoundingClientRect();
      const inView = !(
        rect.left > clientWidth ||
        rect.right < 0 ||
        rect.top > middle ||
        rect.bottom < middle
      );
      tint.classList.toggle(TINTED, inView);
      body.classList.toggle(bodyClass, inView);
    }
  };
  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update);
  update();
}
